import React from 'react';
import { isIos } from '../../platform';

type AppLoaderProps = {
  /** Optional label for screen readers */
  label?: string;
  /** Fullscreen overlay background */
  background?: string;
  /** Control visibility (keeps mounted long enough to fade out) */
  active?: boolean;
  /** Fade duration in ms */
  fadeMs?: number;
};

export const AppLoader: React.FC<AppLoaderProps> = ({
  label = 'Loading',
  background = '#1A1A1C',
  active = true,
  fadeMs = 260,
}) => {
  const [shouldRender, setShouldRender] = React.useState(active);
  const [visible, setVisible] = React.useState(active);

  // Signal that the React boot overlay is painted — native splash can hide now
  const signalledRef = React.useRef(false);
  React.useEffect(() => {
    if (active && !signalledRef.current) {
      signalledRef.current = true;
      // Wait one frame so the overlay is actually on screen before the native
      // splash fades out, avoiding any flash of content beneath both layers.
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('app-loader:visible'));
      });
    }
  }, [active]);

  React.useEffect(() => {
    if (active) {
      setShouldRender(true);
      // next frame -> transition kicks in
      requestAnimationFrame(() => setVisible(true));
      return;
    }

    // fade out
    setVisible(false);
    const t = window.setTimeout(() => setShouldRender(false), fadeMs);
    return () => window.clearTimeout(t);
  }, [active, fadeMs]);

  // On iOS native, the LaunchScreen.storyboard already shows branding —
  // skip the React loader overlay to avoid showing two logos.
  if (!shouldRender || isIos()) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={active ? 'true' : 'false'}
      aria-label={label}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        display: 'grid',
        placeItems: 'center',
        background,
        opacity: visible ? 1 : 0,
        transition: `opacity ${fadeMs}ms ease`,
      }}
    >
      <img
        src="/main-logo-white.svg"
        width={194}
        height={18}
        alt="Mailander"
        draggable={false}
        style={{ display: 'block' }}
      />
    </div>
  );
};
